import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Megaphone, Sparkles, Loader2, Plus, Trash2, ChevronRight, ArrowLeft,
  PlayCircle, Volume2, Wand2, Check, Copy, ChevronDown, Inbox, Mail, DollarSign,
  Send, MessageSquareText, CheckCircle2, XCircle, AlertTriangle, RotateCcw, Music4, Film,
} from "lucide-react";
import PageHeader from "@/components/PageHeader";
import SendToAvatarButton from "@/components/SendToAvatarButton";
import CommercialAvatarPanel from "@/components/commercials/CommercialAvatarPanel";
import { CommercialAddonBanner } from "@/components/commercials/CommercialAddonBanner";
import { ContextualHelp } from "@/components/help/ContextualHelp";
import PromptSuggestionButton from "@/components/PromptSuggestionButton";
import AiWriteScriptModal, { AiWriteButton } from "@/components/AiWriteScriptModal";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, API_BASE } from "@/lib/api";
import { SendToPrompterButton } from "@/components/SendToPrompterButton";
import { toast } from "sonner";
import { appConfirm } from "@/components/AppDialogHost";

const DEFAULT_BRIEF = {
  advertiser: "",
  product: "",
  benefits: "",
  audience: "",
  cta: "",
  duration_sec: 30,
  tone: "energetic",
  language: "English",
  extra_context: "",
};

const STATUS_BADGE = {
  draft:    { label: "DRAFT",     cls: "border-line bg-bg-elevated text-ink-muted" },
  scripted: { label: "SCRIPTED",  cls: "border-violet-500/40 bg-violet-500/10 text-violet-300" },
  voiced:   { label: "VOICED",    cls: "border-sky-500/40 bg-sky-500/10 text-sky-300" },
  mixed:    { label: "MIXED",     cls: "border-brand-amber/50 bg-brand-amber/10 text-brand-amber" },
};

export default function Commercials() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [active, setActive] = useState(null); // full commercial doc
  const [showForm, setShowForm] = useState(false);
  const [brief, setBrief] = useState(DEFAULT_BRIEF);
  const [sponsorBriefs, setSponsorBriefs] = useState([]);
  const [access, setAccess] = useState(null);

  const loadAccess = React.useCallback(() => {
    api.get("/commercials/access").then((r) => setAccess(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadAccess(); }, [loadAccess]);

  const reload = async () => {
    setLoading(true);
    try {
      const [a, b, sp] = await Promise.all([
        api.get("/commercials"),
        api.get("/commercials/meta"),
        api.get("/sponsor-briefs").catch(() => ({ data: [] })),
      ]);
      setList(a.data || []);
      setMeta(b.data);
      setSponsorBriefs(sp.data || []);
    } catch {
      toast.error("Failed to load commercials");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); }, []);

  // Update #163 — honor `?open=<commercialId>` query param so the
  // "Back to commercial" breadcrumb in the Production Suite lands on
  // the specific ad's detail view instead of the empty list.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const id = new URLSearchParams(window.location.search).get("open");
    if (!id) return;
    openCommercial(id);
    // Strip the query param so a subsequent refresh doesn't re-trigger.
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete("open");
      window.history.replaceState({}, "", url);
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const create = async () => {
    if (!brief.advertiser.trim()) {
      toast.error("Advertiser name is required");
      return;
    }
    setCreating(true);
    try {
      const { data } = await api.post("/commercials", brief);
      toast.success("Brief saved");
      setActive(data);
      setShowForm(false);
      setBrief(DEFAULT_BRIEF);
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setCreating(false);
    }
  };

  const openCommercial = async (commercial_id) => {
    try {
      const { data } = await api.get(`/commercials/${commercial_id}`);
      setActive(data);
    } catch {
      toast.error("Could not open commercial");
    }
  };

  const remove = async (commercial_id) => {
    if (!await appConfirm("Delete this commercial?")) return;
    try {
      await api.delete(`/commercials/${commercial_id}`);
      toast.success("Deleted");
      if (active?.commercial_id === commercial_id) setActive(null);
      await reload();
    } catch {
      toast.error("Failed");
    }
  };

  // ===== Detail view =====
  if (active) {
    return (
      <CommercialDetail
        meta={meta}
        commercial={active}
        onBack={() => { setActive(null); reload(); }}
        onChange={setActive}
        onDelete={() => remove(active.commercial_id)}
        navigate={navigate}
      />
    );
  }

  // ===== List view =====
  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto animate-fade-up" data-testid="commercials-page">
      <PageHeader
        eyebrow="// AI COMMERCIAL CREATOR"
        title="Ad Spots, on autopilot"
        description="Take a sponsor brief, generate three on-brand script options, lay down the voice with AI TTS, and ship a mixed spot to Imaging Studio in one click."
        action={
          <button
            onClick={() => { setShowForm(true); setBrief(DEFAULT_BRIEF); }}
            disabled={access ? !access.allowed : false}
            data-testid="commercial-new-btn"
            className="inline-flex items-center gap-2 rounded-md bg-brand-amber px-5 py-2.5 font-semibold text-black amber-glow disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Plus className="h-4 w-4" /> New Commercial
          </button>
        }
      />

      <CommercialAddonBanner access={access} onRefresh={loadAccess} />

      <div className="mt-4 flex justify-end">
        <ContextualHelp featureKey="commercials" />
      </div>

      {showForm && (
        <BriefForm
          meta={meta}
          brief={brief}
          setBrief={setBrief}
          onCancel={() => setShowForm(false)}
          onSubmit={create}
          creating={creating}
        />
      )}

      <SponsorInbox briefs={sponsorBriefs} list={list} openCommercial={openCommercial} />

      <div className="mt-8" data-testid="commercials-list">
        {loading ? (
          <div className="flex items-center gap-2 text-ink-muted text-sm">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : list.length === 0 ? (
          <div className="rounded-xl border border-dashed border-line bg-bg-elevated/40 p-10 text-center">
            <Megaphone className="h-8 w-8 text-brand-amber mx-auto mb-3" />
            <div className="font-display text-xl mb-1">No commercials yet</div>
            <div className="text-sm text-ink-muted mb-4">Brief your first sponsor and let AI write the spot in under a minute.</div>
            <button
              onClick={() => { setShowForm(true); setBrief(DEFAULT_BRIEF); }}
              disabled={access ? !access.allowed : false}
              data-testid="commercial-empty-cta"
              className="inline-flex items-center gap-2 rounded-md bg-brand-amber px-4 py-2 text-sm font-semibold text-black amber-glow disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Plus className="h-4 w-4" /> Start a brief
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {list.map((c) => {
              const badge = STATUS_BADGE[c.status] || STATUS_BADGE.draft;
              return (
                <div
                  key={c.commercial_id}
                  className="rounded-xl border border-line bg-bg-surface p-5 hover:border-brand-amber/40 transition group"
                  data-testid={`commercial-card-${c.commercial_id}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div className="min-w-0">
                      <div className="font-display font-bold text-ink-primary truncate">{c.advertiser}</div>
                      <div className="text-xs text-ink-muted truncate">
                        {c.product || "—"} · {c.duration_sec}s · {c.tone}
                      </div>
                    </div>
                    <span className={`shrink-0 rounded-md border px-2 py-0.5 font-mono text-[10px] tracking-wider ${badge.cls}`}>
                      {badge.label}
                    </span>
                  </div>
                  {c.cta && (
                    <div className="text-xs text-ink-secondary line-clamp-2 mb-4">{c.cta}</div>
                  )}
                  <div className="flex items-center justify-between text-xs">
                    <button
                      onClick={() => openCommercial(c.commercial_id)}
                      data-testid={`commercial-open-${c.commercial_id}`}
                      className="inline-flex items-center gap-1 text-brand-amber hover:underline"
                    >
                      Open <ChevronRight className="h-3 w-3" />
                    </button>
                    <button
                      onClick={() => remove(c.commercial_id)}
                      data-testid={`commercial-delete-${c.commercial_id}`}
                      className="text-ink-muted hover:text-brand-onair"
                      title="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// =========== Brief form ===========
function BriefForm({ meta, brief, setBrief, onCancel, onSubmit, creating }) {
  const update = (k, v) => setBrief((b) => ({ ...b, [k]: v }));
  const durations = meta?.durations || [{ id: 15 }, { id: 30 }, { id: 60 }];
  const tones = meta?.tones || ["energetic"];

  return (
    <div className="mt-6 rounded-xl border border-line bg-bg-surface p-6 grid md:grid-cols-2 gap-4" data-testid="commercial-brief-form">
      <Field
        label="Advertiser *"
        value={brief.advertiser}
        onChange={(v) => update("advertiser", v)}
        testid="brief-advertiser"
        suggest={<PromptSuggestionButton surface="commercial" fieldName="advertiser" context={brief} onSelect={(v) => update("advertiser", v)} testid="brief-suggest-advertiser" />}
      />
      <Field
        label="Product / Service"
        value={brief.product}
        onChange={(v) => update("product", v)}
        testid="brief-product"
        suggest={<PromptSuggestionButton surface="commercial" fieldName="product" context={brief} onSelect={(v) => update("product", v)} testid="brief-suggest-product" />}
      />

      <div className="md:col-span-2">
        <div className="flex items-center justify-between gap-2">
          <Label className="text-xs uppercase tracking-wider text-ink-secondary">Key benefits</Label>
          <PromptSuggestionButton
            surface="commercial"
            fieldName="benefits"
            context={brief}
            onSelect={(v) => update("benefits", v)}
            testid="brief-suggest-benefits"
          />
        </div>
        <Textarea
          rows={2}
          value={brief.benefits}
          onChange={(e) => update("benefits", e.target.value)}
          placeholder="What makes them different? (e.g. 18hr cold brew, no acid, locally roasted)"
          className="mt-1 bg-bg border-line"
          data-testid="brief-benefits"
        />
      </div>

      <Field
        label="Target audience"
        value={brief.audience}
        onChange={(v) => update("audience", v)}
        testid="brief-audience"
        suggest={<PromptSuggestionButton surface="commercial" fieldName="audience" context={brief} onSelect={(v) => update("audience", v)} testid="brief-suggest-audience" />}
      />
      <Field
        label="Call to action (CTA)"
        value={brief.cta}
        onChange={(v) => update("cta", v)}
        testid="brief-cta"
        suggest={<PromptSuggestionButton surface="commercial" fieldName="cta" context={brief} onSelect={(v) => update("cta", v)} testid="brief-suggest-cta" />}
      />

      <div>
        <Label className="text-xs uppercase tracking-wider text-ink-secondary">Duration</Label>
        <Select value={String(brief.duration_sec)} onValueChange={(v) => update("duration_sec", Number(v))}>
          <SelectTrigger className="mt-1 bg-bg border-line" data-testid="brief-duration">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {durations.map((d) => (
              <SelectItem key={d.id} value={String(d.id)} data-testid={`brief-duration-${d.id}`}>
                {d.label || `${d.id}s`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="text-xs uppercase tracking-wider text-ink-secondary">Tone</Label>
        <Select value={brief.tone} onValueChange={(v) => update("tone", v)}>
          <SelectTrigger className="mt-1 bg-bg border-line" data-testid="brief-tone">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {tones.map((t) => (
              <SelectItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="md:col-span-2">
        <div className="flex items-center justify-between gap-2">
          <Label className="text-xs uppercase tracking-wider text-ink-secondary">Extra context (optional)</Label>
          <PromptSuggestionButton
            surface="commercial"
            fieldName="extra_context"
            context={brief}
            onSelect={(v) => update("extra_context", v)}
            testid="brief-suggest-extra"
          />
        </div>
        <Textarea
          rows={2}
          value={brief.extra_context}
          onChange={(e) => update("extra_context", e.target.value)}
          placeholder="Promo dates, location, restrictions, mandatory legal lines…"
          className="mt-1 bg-bg border-line"
          data-testid="brief-extra"
        />
      </div>

      <div className="md:col-span-2 flex items-center justify-end gap-2 pt-2">
        <button
          onClick={onCancel}
          data-testid="brief-cancel"
          className="rounded-md border border-line bg-bg-elevated px-4 py-2 text-sm text-ink-secondary hover:text-ink-primary"
        >
          Cancel
        </button>
        <button
          onClick={onSubmit}
          disabled={creating}
          data-testid="brief-submit"
          className="inline-flex items-center gap-2 rounded-md bg-brand-amber px-5 py-2 text-sm font-semibold text-black amber-glow disabled:opacity-50"
        >
          {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          Save brief
        </button>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, suggest }) {
  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <Label className="text-xs uppercase tracking-wider text-ink-secondary">{label}</Label>
        {suggest || null}
      </div>
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1 bg-bg border-line" data-testid={testid} />
    </div>
  );
}

// =========== Detail view ===========
function CommercialDetail({ meta, commercial, onBack, onChange, onDelete, navigate }) {
  const [c, setC] = useState(commercial);
  const [busy, setBusy] = useState(null); // 'scripts' | 'voice' | 'mix' | 'save'
  const [editText, setEditText] = useState("");
  const [voice, setVoice] = useState(c.voice || "nova");
  const [mixStyle, setMixStyle] = useState("smooth");
  const [bedAssetId, setBedAssetId] = useState("auto");
  const [beds, setBeds] = useState([]);
  const [aiOpen, setAiOpen] = useState(false);

  useEffect(() => {
    api.get("/imaging/assets", { params: { kind: "bed" } })
      .then(({ data }) => {
        const all = Array.isArray(data) ? data : [];
        // Licensed Epidemic imports first, then other beds, capped for usability
        const sorted = [...all.filter((b) => b.source === "epidemic_sound"), ...all.filter((b) => b.source !== "epidemic_sound")];
        setBeds(sorted.slice(0, 60));
      })
      .catch(() => setBeds([]));
  }, []);

  useEffect(() => {
    setC(commercial);
    setVoice(commercial.voice || "nova");
  }, [commercial]);

  const chosen = useMemo(
    () => (c.scripts || []).find((s) => s.id === c.chosen_script_id) || null,
    [c]
  );

  useEffect(() => {
    setEditText(chosen?.text || "");
  }, [chosen?.id]);

  const voices = (meta?.voices || []).filter((v) => !v.needs_key);
  const mixStyles = meta?.mix_styles || [{ id: "smooth", label: "Smooth" }];
  const badge = STATUS_BADGE[c.status] || STATUS_BADGE.draft;
  const audioSrc = c.audio_asset_id ? `${API_BASE}/imaging/assets/${c.audio_asset_id}/audio` : null;

  const generateScripts = async () => {
    setBusy("scripts");
    try {
      const { data } = await api.post(`/commercials/${c.commercial_id}/scripts/generate`);
      setC(data);
      onChange(data);
      toast.success(`Generated ${data.scripts.length} script options`);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "object" ? d.message : d || "Generation failed");
    } finally {
      setBusy(null);
    }
  };

  // Regenerate ONE variation, keeping the other two intact. Saves credits
  // + preserves scripts the user was about to pick.
  const regenerateOneScript = async (scriptId) => {
    setBusy(`re-${scriptId}`);
    try {
      const { data } = await api.post(
        `/commercials/${c.commercial_id}/scripts/${scriptId}/regenerate`,
      );
      setC(data);
      onChange(data);
      toast.success("Fresh variation ready");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "object" ? d.message : d || "Regenerate failed");
    } finally {
      setBusy(null);
    }
  };

  const pickScript = async (scriptId) => {
    setBusy("save");
    try {
      const { data } = await api.patch(`/commercials/${c.commercial_id}`, { chosen_script_id: scriptId });
      setC(data);
      onChange(data);
    } catch {
      toast.error("Failed to pick");
    } finally {
      setBusy(null);
    }
  };

  const saveEdits = async () => {
    if (!chosen) return;
    setBusy("save");
    try {
      const { data } = await api.patch(`/commercials/${c.commercial_id}`, {
        chosen_script_id: chosen.id,
        chosen_script_text: editText,
      });
      setC(data);
      onChange(data);
      toast.success("Script saved");
    } catch {
      toast.error("Failed to save");
    } finally {
      setBusy(null);
    }
  };

  const generateVoice = async () => {
    if (!chosen) { toast.error("Pick a script first"); return; }
    setBusy("voice");
    try {
      const { data } = await api.post(`/commercials/${c.commercial_id}/voice`, { voice, hd: false });
      setC(data);
      onChange(data);
      toast.success("Voiceover ready");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "object" ? d.message : d || "Voice generation failed");
    } finally {
      setBusy(null);
    }
  };

  const sendToImaging = async () => {
    if (!c.audio_asset_id) { toast.error("Generate voice first"); return; }
    setBusy("mix");
    try {
      const { data } = await api.post(`/commercials/${c.commercial_id}/to-imaging`, {
        mix_style: mixStyle,
        ...(bedAssetId !== "auto" ? { bed_asset_id: bedAssetId } : {}),
      });
      toast.success(data.bed_used ? "Mixed with music bed" : "Voice spot sent to Imaging");
      navigate(`/dashboard/imaging?tab=sequencer&project=${data.project_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Mix failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto animate-fade-up space-y-6" data-testid="commercial-detail">
      <button
        onClick={onBack}
        data-testid="commercial-back-btn"
        className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted hover:text-cyan-300 transition"
      >
        <ArrowLeft className="h-3 w-3" /> All commercials
      </button>

      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-amber/80 mb-1">// COMMERCIAL</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">{c.advertiser}</h1>
          <div className="mt-2 flex items-center gap-3 text-sm text-ink-muted">
            <span>{c.duration_sec}s</span>
            <span>·</span>
            <span className="capitalize">{c.tone}</span>
            <span>·</span>
            <span className={`rounded-md border px-2 py-0.5 font-mono text-[10px] tracking-wider ${badge.cls}`} data-testid="commercial-status">
              {badge.label}
            </span>
          </div>
        </div>
        <button
          onClick={onDelete}
          data-testid="commercial-delete-btn"
          className="text-ink-muted hover:text-brand-onair"
          title="Delete"
        >
          <Trash2 className="h-5 w-5" />
        </button>
      </div>

      {/* STEP 1 — Scripts */}
      <section className="rounded-xl border border-line bg-bg-surface p-6" data-testid="step-scripts">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-amber/80">// STEP 1</div>
            <div className="font-display text-xl font-bold">AI Script Generator</div>
            <p className="text-xs text-ink-muted">Three creative angles — pick your favourite or write your own.</p>
          </div>
          <button
            onClick={generateScripts}
            disabled={busy === "scripts"}
            data-testid="commercial-generate-scripts"
            className="inline-flex items-center gap-2 rounded-md bg-brand-amber px-4 py-2 text-sm font-semibold text-black amber-glow disabled:opacity-50"
          >
            {busy === "scripts" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {c.scripts?.length ? "Regenerate" : "Generate 3 scripts"}
          </button>
        </div>

        {(c.scripts || []).length === 0 ? (
          <div className="text-sm text-ink-muted py-6 text-center border border-dashed border-line rounded-md">
            No scripts yet. Click <span className="text-brand-amber font-semibold">Generate</span> to spin up three on-brand variations.
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            {c.scripts.map((s) => {
              const active = s.id === c.chosen_script_id;
              const rerolling = busy === `re-${s.id}`;
              return (
                <div
                  key={s.id}
                  data-testid={`script-card-${s.id}`}
                  className={`text-left rounded-lg border p-4 transition relative ${
                    active
                      ? "border-brand-amber bg-brand-amber/5 ring-1 ring-brand-amber/30"
                      : "border-line bg-bg-elevated hover:border-brand-amber/40"
                  }`}
                >
                  {active && (
                    <div className="absolute top-3 right-3 rounded-full bg-brand-amber/20 p-1">
                      <Check className="h-3 w-3 text-brand-amber" />
                    </div>
                  )}
                  {/* Reroll THIS variation only — keeps the other two. */}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); if (!rerolling) regenerateOneScript(s.id); }}
                    disabled={rerolling || busy === "scripts"}
                    data-testid={`script-reroll-${s.id}`}
                    className="absolute top-3 right-10 rounded-md border border-line bg-bg px-1.5 py-1 text-ink-muted hover:text-brand-amber hover:border-brand-amber/40 disabled:opacity-50 transition"
                    title="Regenerate just this variation"
                    aria-label="Regenerate this variation"
                  >
                    {rerolling ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />}
                  </button>
                  <button
                    type="button"
                    onClick={() => pickScript(s.id)}
                    data-testid={`script-pick-${s.id}`}
                    className="block w-full text-left"
                  >
                    <div className="font-display text-sm font-bold text-ink-primary mb-1 pr-16">{s.name}</div>
                    <div className="text-[10px] font-mono uppercase text-ink-muted mb-2">{s.angle || "—"} · {s.est_words} words</div>
                    {s.hook && (
                      <div className="text-xs text-brand-amber/80 italic mb-2 line-clamp-2">&ldquo;{s.hook}&rdquo;</div>
                    )}
                    <p className="text-xs text-ink-secondary line-clamp-5 whitespace-pre-wrap">{s.text}</p>
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {chosen && (
          <div className="mt-5 rounded-lg border border-line bg-bg-elevated p-4" data-testid="script-editor">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs uppercase tracking-wider text-ink-secondary">
                Editing: <span className="text-brand-amber">{chosen.name}</span>
              </Label>
              <div className="flex items-center gap-2">
                <AiWriteButton onClick={() => setAiOpen(true)} testid="commercial-ai-write" label="Write for me" />
                <button
                  onClick={() => { navigator.clipboard.writeText(editText); toast.success("Copied"); }}
                  data-testid="script-copy"
                  className="inline-flex items-center gap-1 rounded-md border border-line bg-bg px-2 py-1 text-xs text-ink-secondary hover:text-cyan-300 transition"
                >
                  <Copy className="h-3 w-3" /> Copy
                </button>
                <SendToPrompterButton
                  title={`${c.name || "Commercial"} — ${chosen.name}`}
                  text={editText}
                  source="commercial_creator"
                  testid="commercial-send-prompter"
                />
                <SendToAvatarButton
                  script={editText}
                  title={`${c.name || "Commercial"} — ${chosen.name}`}
                  variant="compact"
                  testid="commercial-send-to-avatar"
                />
                <button
                  onClick={saveEdits}
                  disabled={busy === "save"}
                  data-testid="script-save"
                  className="inline-flex items-center gap-1 rounded-md bg-brand-amber/20 border border-brand-amber/40 px-2 py-1 text-xs text-brand-amber hover:bg-brand-amber/30 disabled:opacity-50"
                >
                  {busy === "save" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Save
                </button>
              </div>
            </div>
            <Textarea
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              rows={6}
              className="bg-bg border-line font-mono text-xs"
              data-testid="script-edit-textarea"
            />
          </div>
        )}
      </section>

      {/* STEP 2 — Voice */}
      <section className="rounded-xl border border-line bg-bg-surface p-6" data-testid="step-voice">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-amber/80">// STEP 2</div>
            <div className="font-display text-xl font-bold">AI Voiceover</div>
            <p className="text-xs text-ink-muted">Pick a voice and we&apos;ll cut the read in seconds.</p>
          </div>
          <button
            onClick={generateVoice}
            disabled={busy === "voice" || !chosen}
            data-testid="commercial-generate-voice"
            className="inline-flex items-center gap-2 rounded-md bg-brand-amber px-4 py-2 text-sm font-semibold text-black amber-glow disabled:opacity-50"
          >
            {busy === "voice" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Volume2 className="h-4 w-4" />}
            {c.audio_asset_id ? "Regenerate voice" : "Generate voice"}
          </button>
        </div>

        <div className="grid md:grid-cols-3 gap-3 items-end">
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-wider text-ink-secondary">Voice talent</Label>
            <Select value={voice} onValueChange={setVoice}>
              <SelectTrigger className="mt-1 bg-bg border-line" data-testid="commercial-voice-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-80">
                {voices.map((v) => (
                  <SelectItem key={v.id} value={v.id} data-testid={`voice-opt-${v.id}`}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {audioSrc && (
            <div className="md:col-span-3 mt-1 rounded-md border border-line bg-bg-elevated p-3" data-testid="commercial-audio-block">
              <div className="flex items-center gap-2 text-xs text-ink-muted mb-1.5">
                <PlayCircle className="h-3.5 w-3.5 text-brand-amber" />
                Voiceover · {c.voice_label}
              </div>
              <audio controls src={audioSrc} className="w-full" data-testid="commercial-audio" />
            </div>
          )}
        </div>
      </section>

      {/* STEP 3 — Mix to imaging */}
      <section className="rounded-xl border border-line bg-bg-surface p-6" data-testid="step-mix">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-amber/80">// STEP 3</div>
            <div className="font-display text-xl font-bold">One-Click Mix &amp; Produce</div>
            <p className="text-xs text-ink-muted">Drop the spot into Imaging Studio with a music bed pre-ducked under the voice.</p>
          </div>
          <button
            onClick={sendToImaging}
            disabled={busy === "mix" || !c.audio_asset_id}
            data-testid="commercial-to-imaging"
            title={!c.audio_asset_id ? "Generate the voice in Step 2 first" : "Mix voice + bed and open in Imaging Studio"}
            className="inline-flex items-center gap-2 rounded-md bg-brand-amber px-4 py-2 text-sm font-semibold text-black amber-glow disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy === "mix" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
            Send to Imaging Studio
          </button>
        </div>

        {/* Alternative destination — the new Production Suite DAW.
            Deep-links so the Uploads tab of the Asset Library lands on
            the commercial's voice asset, ready to drag onto any track. */}
        {c.audio_asset_id && (
          <div className="mb-3 rounded-md border border-line bg-bg-elevated px-3 py-2 text-xs flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-ink-secondary">
              <Music4 className="h-3 w-3 text-brand-amber" />
              Prefer the new DAW? Send this voice straight into the <span className="text-ink-primary font-medium">Production Suite</span> and layer your own beds / SFX around it.
            </div>
            <button
              type="button"
              onClick={() => navigate(`/dashboard/production-suite?highlight=${c.audio_asset_id}&commercial=${c.commercial_id}`)}
              data-testid="commercial-to-production-suite"
              className="inline-flex items-center gap-1 rounded-md border border-line bg-bg px-2 py-1 text-brand-amber hover:border-brand-amber/40 whitespace-nowrap"
            >
              Open in Production Suite <ChevronRight className="h-3 w-3" />
            </button>
          </div>
        )}

        {/* Third destination — the AI Avatar Studio. Turns the chosen
            script into a talking-avatar video. Only requires the SCRIPT
            (not the voice), so it lights up as soon as the user picks a
            variation in Step 1. */}
        {chosen && (chosen.text || editText) && (
          <div className="mb-3 rounded-md border border-line bg-bg-elevated px-3 py-2 text-xs flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-ink-secondary">
              <Film className="h-3 w-3 text-brand-amber" />
              Want a video version too? Render this script as a talking-avatar spot in the <span className="text-ink-primary font-medium">AI Avatar Studio</span>.
            </div>
            <SendToAvatarButton
              script={editText || chosen.text}
              title={`${c.advertiser || "Commercial"} — ${chosen.name}`}
              variant="compact"
              testid="commercial-to-avatar-studio"
            />
          </div>
        )}

        {/* Helpful prompt when the button is disabled — tells the user
            exactly which prerequisite step they still owe. */}
        {!c.audio_asset_id && (
          <div className="mb-3 rounded-md border border-brand-amber/30 bg-brand-amber/5 px-3 py-2 text-xs text-brand-amber/90" data-testid="commercial-to-imaging-locked-hint">
            <AlertTriangle className="h-3 w-3 inline-block mr-1 -mt-0.5" />
            Generate the voice in <strong>Step 2</strong> first — then this button lights up and drops your spot into the Imaging Studio with a music bed pre-ducked underneath.
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-wider text-ink-secondary">Mix style</Label>
            <Select value={mixStyle} onValueChange={setMixStyle}>
              <SelectTrigger className="mt-1 bg-bg border-line" data-testid="commercial-mix-style">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {mixStyles.map((m) => (
                  <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-ink-secondary">Music bed</Label>
            <Select value={bedAssetId} onValueChange={setBedAssetId}>
              <SelectTrigger className="mt-1 bg-bg border-line" data-testid="commercial-bed-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-80">
                <SelectItem value="auto" data-testid="bed-opt-auto">Auto-pick (match station vibe)</SelectItem>
                {beds.map((b) => (
                  <SelectItem key={b.asset_id} value={b.asset_id} data-testid={`bed-opt-${b.asset_id}`}>
                    {b.label}
                    {b.source === "epidemic_sound" ? " · Epidemic Sound" : ""}
                    {b.duration_seconds ? ` · ${Math.round(b.duration_seconds)}s` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {beds.length === 0 && (
              <p className="mt-1 text-[11px] text-ink-muted">
                No beds in your library yet — import licensed tracks from the <span className="text-brand-amber">Audio Studio</span> ("Bed" button) or upload beds in Imaging.
              </p>
            )}
          </div>
          {c.imaging_project_id && (
            <div className="rounded-md border border-line bg-bg-elevated p-3 text-xs flex items-center justify-between">
              <span className="text-ink-secondary">Last produced project:</span>
              <button
                onClick={() => navigate(`/dashboard/imaging?tab=sequencer&project=${c.imaging_project_id}`)}
                data-testid="commercial-open-project"
                className="inline-flex items-center gap-1 text-brand-amber hover:underline"
              >
                Open in Imaging <ChevronRight className="h-3 w-3" />
              </button>
            </div>
          )}
        </div>
      </section>

      {/* STEP 3B — AI Avatar (inline). Renders the chosen script into a
          talking-avatar video without ever leaving the Commercials page. */}
      {chosen && (chosen.text || editText) && (
        <section className="rounded-xl border border-line bg-bg-surface p-6" data-testid="step-avatar">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-amber/80">// STEP 3B · OPTIONAL</div>
              <div className="font-display text-xl font-bold flex items-center gap-2">
                <Film className="h-5 w-5 text-brand-amber" />
                AI Avatar Video
              </div>
              <p className="text-xs text-ink-muted">
                Render this exact script into a broadcast-ready talking-avatar MP4 — perfect for social cutdowns &amp; sponsor previews.
              </p>
            </div>
          </div>
          <CommercialAvatarPanel
            commercial={c}
            chosenScriptText={editText || chosen.text}
            defaultTitle={`${c.advertiser || "Commercial"} — ${chosen.name}`}
          />
        </section>
      )}

      {/* STEP 4 — Send preview to sponsor (only if there's a sponsor email) */}
      {c.audio_asset_id && (c.sponsor_email || c.source === "sponsor_brief") && (
        <SendPreviewPanel commercial={c} onSent={(updated) => { setC(updated); onChange(updated); }} />
      )}

      <AiWriteScriptModal
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        defaultScriptType="commercial"
        defaultTone={c.brief?.tone || "energetic"}
        defaultLengthSec={c.brief?.duration_sec || 30}
        initialTopic={[
          c.brief?.advertiser && `Advertiser: ${c.brief.advertiser}`,
          c.brief?.product && `Product/Service: ${c.brief.product}`,
          c.brief?.benefits && `Key benefits: ${c.brief.benefits}`,
          c.brief?.audience && `Audience: ${c.brief.audience}`,
          c.brief?.cta && `CTA: ${c.brief.cta}`,
        ].filter(Boolean).join("\n")}
        hideTitle
        onApply={({ script }) => {
          setEditText(script);
          setAiOpen(false);
          toast.success("Script inserted. Tweak it, then Save.");
        }}
      />
    </div>
  );
}

// ============ Send Preview Panel ============
function SendPreviewPanel({ commercial, onSent }) {
  const c = commercial;
  const [sending, setSending] = useState(false);
  const [note, setNote] = useState("");
  const [recipient, setRecipient] = useState(c.sponsor_email || "");
  const [last, setLast] = useState({
    preview_url: null, whatsapp_url: null, email_sent: null, email_error: null,
  });
  const status = c.preview_status;     // pending | approved | rejected
  const sentAt = c.preview_sent_at ? new Date(c.preview_sent_at).toLocaleString() : null;

  const statusBadge = () => {
    if (status === "approved") {
      return <span className="inline-flex items-center gap-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 px-2 py-0.5 font-mono text-[10px]"><CheckCircle2 className="h-3 w-3" /> APPROVED</span>;
    }
    if (status === "rejected") {
      return <span className="inline-flex items-center gap-1 rounded-md border border-rose-500/40 bg-rose-500/10 text-rose-300 px-2 py-0.5 font-mono text-[10px]"><XCircle className="h-3 w-3" /> CHANGES REQUESTED</span>;
    }
    if (status === "pending") {
      return <span className="inline-flex items-center gap-1 rounded-md border border-sky-500/40 bg-sky-500/10 text-sky-300 px-2 py-0.5 font-mono text-[10px]"><Loader2 className="h-3 w-3 animate-spin" /> AWAITING SPONSOR</span>;
    }
    return null;
  };

  const send = async () => {
    if (!recipient.trim()) { toast.error("Recipient email required"); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/commercials/${c.commercial_id}/send-preview`, {
        recipient_email: recipient.trim(),
        note,
      });
      setLast(data);
      if (data.email_sent) {
        toast.success(`Email sent to ${recipient}`);
      } else if (data.email_error?.includes("RESEND_API_KEY")) {
        toast.info("Email is scaffolded — drop in your Resend key to enable auto-send");
      } else if (data.email_error) {
        toast.error(`Email failed: ${data.email_error.slice(0, 80)}`);
      }
      // Re-fetch the commercial so the status row reflects the new send timestamp + token
      const { data: refreshed } = await api.get(`/commercials/${c.commercial_id}`);
      onSent(refreshed);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send preview");
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="rounded-xl border border-line bg-bg-surface p-6" data-testid="step-preview">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-amber/80">// STEP 4</div>
          <div className="font-display text-xl font-bold flex items-center gap-2">
            Send preview to sponsor {statusBadge()}
          </div>
          <p className="text-xs text-ink-muted">Email a hosted preview page (or share via WhatsApp) — sponsor clicks Approve or asks for changes, no account needed.</p>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-3 items-end">
        <div className="md:col-span-2">
          <Label className="text-xs uppercase tracking-wider text-ink-secondary">Recipient email</Label>
          <Input
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            type="email"
            placeholder="sponsor@business.com"
            className="mt-1 bg-bg border-line"
            data-testid="preview-recipient"
          />
        </div>
        <button
          onClick={send}
          disabled={sending}
          data-testid="preview-send-btn"
          className="inline-flex items-center justify-center gap-2 rounded-md bg-brand-amber px-4 py-2.5 text-sm font-semibold text-black amber-glow disabled:opacity-50"
        >
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          {c.preview_sent_at ? "Resend preview" : "Send preview"}
        </button>
      </div>

      <div className="mt-3">
        <Label className="text-xs uppercase tracking-wider text-ink-secondary">Personal note (optional)</Label>
        <Textarea
          rows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Hey Joe — give this a listen and let us know what you think!"
          className="mt-1 bg-bg border-line"
          data-testid="preview-note"
        />
      </div>

      {/* Result strip */}
      {(last.preview_url || c.preview_sent_at) && (
        <div className="mt-4 rounded-lg border border-line bg-bg-elevated p-4 space-y-2.5" data-testid="preview-result">
          {last.email_error?.includes("RESEND_API_KEY") && (
            <div className="flex items-start gap-2 rounded-md border border-cyan-500/40 bg-cyan-500/10 p-2.5 text-[11px] text-cyan-100">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>Email auto-send is scaffolded but Resend isn&apos;t configured yet. Add <code className="font-mono">RESEND_API_KEY</code> in backend env. Until then, share the preview link manually below.</span>
            </div>
          )}
          {last.preview_url && (
            <PreviewLinkRow label="Preview link" url={last.preview_url} testid="preview-link" />
          )}
          {last.whatsapp_url && (
            <div className="flex items-center gap-2 justify-between">
              <span className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">WhatsApp</span>
              <a
                href={last.whatsapp_url}
                target="_blank"
                rel="noreferrer"
                data-testid="preview-whatsapp"
                className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-1.5 text-xs font-semibold text-emerald-300 hover:bg-emerald-500/20 transition"
              >
                <MessageSquareText className="h-3.5 w-3.5" /> Open in WhatsApp
              </a>
            </div>
          )}
          {sentAt && !last.preview_url && (
            <div className="text-[11px] text-ink-muted">Last sent {sentAt} to <span className="text-ink-primary">{c.preview_sent_to}</span></div>
          )}
        </div>
      )}

      {/* Sponsor feedback (if any) */}
      {c.preview_feedback && (
        <div className="mt-4 rounded-md border border-line bg-bg-elevated p-3" data-testid="preview-sponsor-feedback">
          <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-1">// SPONSOR NOTE</div>
          <p className="text-sm text-ink-secondary italic">&ldquo;{c.preview_feedback}&rdquo;</p>
        </div>
      )}
    </section>
  );
}

function PreviewLinkRow({ label, url, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="flex items-center gap-2">
      <span className="shrink-0 font-mono text-[10px] uppercase tracking-wider text-ink-muted">{label}</span>
      <code className="flex-1 truncate font-mono text-[11px] text-brand-amber/90 bg-bg/60 border border-line rounded px-2 py-1">{url}</code>
      <button onClick={copy} data-testid={testid} className="inline-flex items-center gap-1 rounded-md border border-line bg-bg px-2 py-1 text-xs text-ink-secondary hover:text-cyan-300 transition">
        {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}

// ============ Sponsor Inbox ============
function SponsorInbox({ briefs, list, openCommercial }) {
  const paid = (briefs || []).filter((b) => b.status === "paid");
  if (!paid.length) return null;

  const fmtMoney = (amount, currency) => {
    try {
      return new Intl.NumberFormat("en-US", { style: "currency", currency: (currency || "USD").toUpperCase() }).format(amount);
    } catch { return `$${(amount || 0).toFixed(2)}`; }
  };

  const totalRevenue = paid.reduce((s, b) => s + Number(b.price_amount || 0), 0);
  const currency = paid[0]?.currency || "usd";

  const findCommercial = (cid) => (list || []).find((c) => c.commercial_id === cid);

  return (
    <section className="mt-8 rounded-xl border border-brand-amber/30 bg-gradient-to-br from-brand-amber/5 to-transparent p-6" data-testid="sponsor-inbox">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-md bg-brand-amber/20 flex items-center justify-center">
            <Inbox className="h-5 w-5 text-brand-amber" />
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-amber/80">// SPONSOR INBOX</div>
            <div className="font-display text-xl font-bold">{paid.length} paid {paid.length === 1 ? "brief" : "briefs"} waiting for production</div>
          </div>
        </div>
        <div className="text-right">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">// REVENUE</div>
          <div className="font-display text-2xl font-black text-brand-amber" data-testid="sponsor-inbox-revenue">{fmtMoney(totalRevenue, currency)}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {paid.slice(0, 6).map((b) => {
          const c = findCommercial(b.commercial_id);
          return (
            <div
              key={b.brief_id}
              className="rounded-lg border border-line bg-bg-surface p-4 hover:border-brand-amber/40 transition"
              data-testid={`sponsor-brief-${b.brief_id}`}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <div className="font-display font-bold text-ink-primary truncate">{b.advertiser}</div>
                  <div className="text-[11px] text-ink-muted truncate flex items-center gap-1">
                    <Mail className="h-3 w-3" /> {b.sponsor_email}
                  </div>
                </div>
                <span className="shrink-0 rounded-md border border-brand-amber/40 bg-brand-amber/10 text-brand-amber px-2 py-0.5 font-mono text-[10px] flex items-center gap-1">
                  <DollarSign className="h-3 w-3" />
                  {fmtMoney(b.price_amount, b.currency)}
                </span>
              </div>
              <div className="text-[11px] text-ink-muted mb-3">
                {b.duration_sec}s · {b.tone} · paid {new Date(b.paid_at || b.created_at).toLocaleDateString()}
              </div>
              {b.product && <p className="text-xs text-ink-secondary line-clamp-2 mb-3">{b.product}</p>}
              {c ? (
                <button
                  onClick={() => openCommercial(c.commercial_id)}
                  data-testid={`sponsor-brief-open-${b.brief_id}`}
                  className="w-full inline-flex items-center justify-center gap-1 rounded-md bg-brand-amber px-3 py-1.5 text-xs font-semibold text-black amber-glow"
                >
                  Produce the spot <ChevronRight className="h-3 w-3" />
                </button>
              ) : (
                <div className="text-[10px] font-mono text-ink-muted text-center py-1.5">Linked commercial missing</div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

